import { FormObjectState, ObjectConfig, ObjectState } from "@homebound/form-state";
import { ReactNode } from "react";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { GuidedChangeEventPage_ChangeEventFragment } from "../GuidedChangeEventPage.generated";

// A single step within a workflow. These are pure data: `render` and `isComplete`
// read the forms but must NOT call hooks (all hook logic lives in `useActions`).
export type WorkflowStep<F> = {
  value: GuidedChangeEventStep;
  label: string;
  // Drives the stepper's "complete" checkmark and enables the Next button.
  isComplete: (form: ObjectState<F>) => boolean;
  // Whether the step can be navigated to. Defaults to enabled.
  disabled?: (form: ObjectState<F>) => boolean;
  render: (form: ObjectState<F>) => ReactNode;
};

export type WorkflowFormState<F> = FormObjectState<F, GuidedChangeEventPage_ChangeEventFragment | undefined>;
export type WorkflowActionArgs<F> = { form: WorkflowFormState<F>; projectId: string };

export type WorkflowActions = {
  // Runs when the final "Create" button is clicked.
  onSubmit: () => Promise<void>;
  // Optional side effects run BEFORE advancing past a given step, when Continue is clicked.
  // Returning `false` blocks the advance (e.g. server-side validation failed).
  onContinue?: Partial<Record<GuidedChangeEventStep, () => Promise<boolean | void>>>;
  // Saves partial progress and leaves. Omit it and the "Save & Exit" button isn't rendered.
  onSaveAndExit?: () => Promise<void>;
};

// The contract every workflow implements. `F` is the workflow's own form shape.
export type GuidedWorkflow<F> = {
  workflow: ChangeEventWorkflow;
  formConfig: ObjectConfig<F>;
  steps: WorkflowStep<F>[];
  // Rebuilds the workflow form from an existing change event, so edit mode / a mid-wizard refresh
  // comes back to what was already saved. Workflows that don't save anything yet can skip it.
  mapToForm?: (changeEvent: GuidedChangeEventPage_ChangeEventFragment) => F;
  // A hook, so it may call useMutation / useNavigate / useSnackbar / useModal, etc.
  // Safe to call dynamically because the runner mounts each workflow with a
  // `key={workflow}`, keeping this hook's identity stable per mounted instance.
  useActions: (args: WorkflowActionArgs<F>) => WorkflowActions;
};

// Identity helper. Infers `F` from the passed object literal so a workflow's
// `steps` and `useActions` are fully typed against its own form shape at the
// definition site, with no explicit generic needed.
export function defineWorkflow<F>(workflow: GuidedWorkflow<F>): GuidedWorkflow<F> {
  return workflow;
}
